import React, { useState, useEffect } from 'react';
import { 
  Card, 
  List, 
  Typography, 
  Tag, 
  Space, 
  Button, 
  Empty, 
  Spin, 
  Alert,
  Badge,
  Tooltip,
  Row,
  Col,
  Statistic,
  Progress,
  Select,
  Switch,
  Divider,
  message,
  Collapse
} from 'antd';
import { 
  GlobalOutlined, 
  ClockCircleOutlined, 
  LinkOutlined,
  AlertOutlined,
  FireOutlined,
  ThunderboltOutlined,
  EyeOutlined,
  FilterOutlined,
  SettingOutlined,
  BarChartOutlined,
  DatabaseOutlined
} from '@ant-design/icons';
import { useFirestore } from '../contexts/FirestoreContext';
import { useAuth } from '../contexts/AuthContext';
import MediaIntelligenceService from '../services/mediaIntelligenceService';
import GoogleNewsFeedProvider from '../services/providers/googleNewsFeedProvider';
import ABCNewsFeedProvider from '../services/providers/abcNewsFeedProvider';
import SMHNewsFeedProvider from '../services/providers/smhNewsFeedProvider';
import TheAgeFeedProvider from '../services/providers/theAgeFeedProvider';
import NineNewsFeedProvider from '../services/providers/nineNewsFeedProvider';
import SBSNewsFeedProvider from '../services/providers/sbsNewsFeedProvider';
// import NewsComAuFeedProvider from '../services/providers/newsComAuFeedProvider'; // DISABLED: Legal issues with commercial use
import BasicArticleProcessor from '../services/processors/basicArticleProcessor';
import GPTKeywordExpansionService from '../services/gptKeywordExpansionService';
import { doc, deleteDoc, getDoc, collection, getDocs, updateDoc, serverTimestamp, addDoc } from 'firebase/firestore';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

/**
 * Media Intelligence Tab Component
 * 
 * Displays real-time media monitoring and intelligence for crisis management.
 * Shows relevant news articles, sentiment analysis, and crisis indicators.
 * Features enhanced filtering and content type classification.
 */
const MediaIntelligenceTab = ({ incidentId, incident }) => {
  const { db } = useFirestore();
  const { currentUser } = useAuth();
  
  // State management
  const [articles, setArticles] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState({});
  const [mediaService, setMediaService] = useState(null);
  const [filterSensitivity, setFilterSensitivity] = useState('medium');
  const [showFilteredArticles, setShowFilteredArticles] = useState(false);
  const [organisationData, setOrganisationData] = useState(null); // Track org data
  const [gptKeywordService] = useState(() => new GPTKeywordExpansionService());
  const [expandedKeywords, setExpandedKeywords] = useState([]);
  const [lastSearchSources, setLastSearchSources] = useState([]); // Track searched sources
  const [useClusteredView, setUseClusteredView] = useState(true); // Toggle for clustered vs flat view
  const [expandedClusters, setExpandedClusters] = useState(new Set()); // Track which clusters are expanded
  const [activeAccordionKeys, setActiveAccordionKeys] = useState(['metrics', 'coverage']); // Control accordion state

  /**
   * Sort articles by relevance score (highest first), then by publication date
   */
  const sortArticlesByRelevance = (articlesList) => {
    return [...articlesList].sort((a, b) => {
      // Primary sort: Relevance score (highest first)
      const aRelevance = a.relevanceScore || 0;
      const bRelevance = b.relevanceScore || 0;
      
      if (aRelevance !== bRelevance) {
        return bRelevance - aRelevance; // Descending order (highest relevance first)
      }
      
      // Secondary sort: Publication date (newest first)
      const aDate = new Date(a.publishedAt || 0);
      const bDate = new Date(b.publishedAt || 0);
      return bDate - aDate;
    });
  };

  /**
   * Calculate similarity score between two articles (0-1 scale)
   */
  const calculateArticleSimilarity = (article1, article2) => {
    let similarityScore = 0;
    let totalChecks = 0;

    // 1. Title similarity (40% weight) - Most important
    if (article1.title && article2.title) {
      const title1 = article1.title.toLowerCase().trim();
      const title2 = article2.title.toLowerCase().trim();
      
      // Exact match check
      if (title1 === title2) {
        similarityScore += 0.4;
      } else {
        // Fuzzy similarity using simple word overlap
        const words1 = title1.split(/\s+/).filter(word => word.length > 3); // Ignore short words
        const words2 = title2.split(/\s+/).filter(word => word.length > 3);
        
        if (words1.length > 0 && words2.length > 0) {
          const commonWords = words1.filter(word => words2.includes(word));
          const titleSimilarity = (commonWords.length * 2) / (words1.length + words2.length);
          similarityScore += titleSimilarity * 0.4;
        }
      }
      totalChecks += 0.4;
    }

    // 2. Time proximity (20% weight)
    if (article1.publishedAt && article2.publishedAt) {
      const time1 = new Date(article1.publishedAt).getTime();
      const time2 = new Date(article2.publishedAt).getTime();
      const timeDiff = Math.abs(time1 - time2);
      const sixHours = 6 * 60 * 60 * 1000; // 6 hours in milliseconds
      
      if (timeDiff <= sixHours) {
        const timeProximity = Math.max(0, 1 - (timeDiff / sixHours));
        similarityScore += timeProximity * 0.2;
      }
      totalChecks += 0.2;
    }

    // 3. Location overlap (20% weight)
    if (article1.location && article2.location) {
      const loc1 = article1.location.toLowerCase();
      const loc2 = article2.location.toLowerCase();
      
      if (loc1 === loc2) {
        similarityScore += 0.2;
      } else if (loc1.includes(loc2) || loc2.includes(loc1)) {
        similarityScore += 0.1; // Partial location match
      }
      totalChecks += 0.2;
    }

    // 4. Content type similarity (10% weight)
    if (article1.contentType && article2.contentType) {
      if (article1.contentType === article2.contentType) {
        similarityScore += 0.1;
      }
      totalChecks += 0.1;
    }

    // 5. Relevance score proximity (10% weight)
    if (article1.relevanceScore && article2.relevanceScore) {
      const scoreDiff = Math.abs(article1.relevanceScore - article2.relevanceScore);
      if (scoreDiff <= 0.15) { // Within 15% relevance
        const scoreProximity = 1 - (scoreDiff / 0.15);
        similarityScore += scoreProximity * 0.1;
      }
      totalChecks += 0.1;
    }

    // Normalize by total possible score
    return totalChecks > 0 ? similarityScore / totalChecks : 0;
  };

  /**
   * Deduplicate articles by source, keeping highest relevance per source
   */
  const deduplicateBySource = (articlesList) => {
    console.log(`🔧 SOURCE DEDUPLICATION: Processing ${articlesList.length} articles`);
    
    const sourceGroups = {};
    let duplicatesRemoved = 0;
    
    // Group articles by source and keep highest relevance
    articlesList.forEach(article => {
      const sourceKey = article.source || 'unknown';
      const relevanceScore = article.relevanceScore || 0;
      
      if (!sourceGroups[sourceKey]) {
        sourceGroups[sourceKey] = article;
      } else {
        // Keep higher relevance article, or if same relevance, keep newer one
        const existingRelevance = sourceGroups[sourceKey].relevanceScore || 0;
        const existingDate = new Date(sourceGroups[sourceKey].publishedAt || 0);
        const candidateDate = new Date(article.publishedAt || 0);
        
        if (relevanceScore > existingRelevance || 
            (relevanceScore === existingRelevance && candidateDate > existingDate)) {
          console.log(`🔄 REPLACING: ${sourceKey} article (${Math.round(existingRelevance * 100)}% → ${Math.round(relevanceScore * 100)}%)`);
          sourceGroups[sourceKey] = article;
        } else {
          console.log(`🗑️ REMOVING: Duplicate from ${sourceKey} (${Math.round(relevanceScore * 100)}% vs ${Math.round(existingRelevance * 100)}%)`);
        }
        duplicatesRemoved++;
      }
    });
    
    const deduplicated = Object.values(sourceGroups);
    console.log(`✅ SOURCE DEDUPLICATION COMPLETE: ${articlesList.length} → ${deduplicated.length} articles (${duplicatesRemoved} duplicates removed)`);
    
    return deduplicated;
  };

    /**
* Group similar articles into clusters (ENHANCED: Source-aware)
   */
  const clusterSimilarArticles = (articlesList) => {
    if (!articlesList || articlesList.length === 0) return [];
    
    // 🎯 CORRECT LOGIC: Process ALL articles, dedupe within clusters only
    const allArticles = [...articlesList];
    console.log(`🎯 CLUSTERING: Processing ${allArticles.length} articles - will show up to ${allArticles.length} clusters/articles`);
    
    // 🔍 DEBUG: Look for the ABC pub article specifically
    const abcPubArticle = allArticles.find(article => 
      article.title && article.title.toLowerCase().includes('pub') && 
      article.title.toLowerCase().includes('floor') &&
      article.source && article.source.toLowerCase().includes('abc')
    );
    if (abcPubArticle) {
      console.log(`🎯 FOUND ABC PUB ARTICLE IN CLUSTERING: "${abcPubArticle.title}" (${Math.round(abcPubArticle.relevanceScore * 100)}% relevance)`);
    } else {
      console.log(`❌ ABC PUB ARTICLE NOT FOUND in ${allArticles.length} articles being clustered`);
      console.log(`🔍 Available articles:`, allArticles.map(a => `"${a.title?.substring(0, 50)}..." (${a.source}) ${Math.round((a.relevanceScore || 0) * 100)}%`));
    }
    
    const clusters = [];
    const processed = new Set();
    const SIMILARITY_THRESHOLD = 0.70; // 70% similarity required for clustering (LOWERED: identical articles weren't clustering at 80%)
    
    // Sort ALL articles by relevance first to ensure best primary articles
    const sortedArticles = sortArticlesByRelevance(allArticles);
    
    for (let i = 0; i < sortedArticles.length; i++) {
      if (processed.has(i)) continue;
      
      const primaryArticle = sortedArticles[i];
      const cluster = {
        id: `cluster_${i}`,
        type: 'cluster',
        primaryArticle: primaryArticle,
        supportingArticles: [],
        totalSources: 1,
        clusterRelevanceScore: primaryArticle.relevanceScore || 0,
        earliestPublished: primaryArticle.publishedAt,
        latestPublished: primaryArticle.publishedAt,
        sources: [primaryArticle.source].filter(Boolean)
      };
      
      // Find similar articles
      for (let j = i + 1; j < sortedArticles.length; j++) {
        if (processed.has(j)) continue;
        
        const candidate = sortedArticles[j];
        const similarity = calculateArticleSimilarity(primaryArticle, candidate);
        
        // 🔍 DEBUG: Log high similarity cases
        if (similarity > 0.6) {
          console.log(`🔍 CLUSTERING: "${primaryArticle.title?.substring(0, 40)}..." vs "${candidate.title?.substring(0, 40)}..."`);
          console.log(`   📊 Similarity: ${Math.round(similarity * 100)}% | Threshold: ${Math.round(SIMILARITY_THRESHOLD * 100)}%`);
          console.log(`   🏢 Sources: "${primaryArticle.source}" vs "${candidate.source}"`);
        }
        
        if (similarity >= SIMILARITY_THRESHOLD) {
          // 🎯 SOURCE DEDUPLICATION WITHIN CLUSTER: Only add if source not already in cluster
          const candidateSource = candidate.source || 'unknown';
          const existingSourceArticle = cluster.supportingArticles.find(article => 
            (article.source || 'unknown') === candidateSource
          );
          
          if (!existingSourceArticle && candidateSource !== (primaryArticle.source || 'unknown')) {
            // No existing article from this source in cluster - add it
            cluster.supportingArticles.push({
              ...candidate,
              similarityScore: similarity
            });
            cluster.totalSources++;
            
            // Update cluster metadata
            if (candidate.source && !cluster.sources.includes(candidate.source)) {
              cluster.sources.push(candidate.source);
            }
          } else if (existingSourceArticle) {
            // Source already exists - keep the higher relevance article
            const existingRelevance = existingSourceArticle.relevanceScore || 0;
            const candidateRelevance = candidate.relevanceScore || 0;
            
            if (candidateRelevance > existingRelevance) {
              // Replace existing with higher relevance article
              const index = cluster.supportingArticles.indexOf(existingSourceArticle);
              cluster.supportingArticles[index] = {
                ...candidate,
                similarityScore: similarity
              };
              console.log(`🔄 CLUSTER: Replaced ${candidateSource} article with higher relevance (${Math.round(existingRelevance * 100)}% → ${Math.round(candidateRelevance * 100)}%)`);
            }
          }
          
          // Update time range
          const candidateTime = new Date(candidate.publishedAt).getTime();
          const earliestTime = new Date(cluster.earliestPublished).getTime();
          const latestTime = new Date(cluster.latestPublished).getTime();
          
          if (candidateTime < earliestTime) {
            cluster.earliestPublished = candidate.publishedAt;
          }
          if (candidateTime > latestTime) {
            cluster.latestPublished = candidate.publishedAt;
          }
          
          processed.add(j);
        }
      }
      
      // Sort supporting articles by relevance score
      cluster.supportingArticles.sort((a, b) => (b.relevanceScore || 0) - (a.relevanceScore || 0));
      
      // Determine if this should be a cluster or standalone article
      if (cluster.supportingArticles.length > 0) {
        // This is a true cluster with multiple sources
        cluster.confidence = cluster.totalSources >= 3 ? 'HIGH' : 'MEDIUM';
        clusters.push(cluster);
      } else {
        // This is a standalone article
        clusters.push({
          ...primaryArticle,
          type: 'standalone'
        });
      }
      
      processed.add(i);
    }
    
    // Sort clusters by primary article relevance
    const finalClusters = clusters.sort((a, b) => {
      const aScore = a.type === 'cluster' ? a.clusterRelevanceScore : (a.relevanceScore || 0);
      const bScore = b.type === 'cluster' ? b.clusterRelevanceScore : (b.relevanceScore || 0);
      return bScore - aScore;
    });
    
    console.log(`✅ CLUSTERING COMPLETE: ${allArticles.length} articles → ${finalClusters.length} clusters/articles (${finalClusters.filter(c => c.type === 'cluster').length} clusters, ${finalClusters.filter(c => c.type === 'standalone').length} standalone)`);
    return finalClusters;
  };

  // Initialize Media Intelligence Service with enhanced configuration
  useEffect(() => {
    if (!db) return;

    try {
      console.log('🔍 Initializing Enhanced Media Intelligence Service...');
      
      // Create service instance with enhanced filtering
      const service = new MediaIntelligenceService(db, {
        // DON'T OVERRIDE - Let filterSensitivity control the threshold
        filterSensitivity: 'medium',  // Medium = 50% threshold (not overridden!)
        enableAdvancedFiltering: true,
        storeFilteredArticles: true, // For debugging and analysis
        maxArticlesPerCycle: 500 // INCREASED FOR DEBUGGING - process all articles
      });
      
      // Register providers - Multi-source Australian media intelligence
      console.log('📰 Registering Australian media feed providers...');
      
      // Google News (existing - keep as fallback)
      const googleProvider = new GoogleNewsFeedProvider();
      service.registerFeedProvider(googleProvider);
      
      // Australian Broadcasting Corporation (ABC News)
      const abcProvider = new ABCNewsFeedProvider();
      service.registerFeedProvider(abcProvider);
      
      // Nine Entertainment Network
      const smhProvider = new SMHNewsFeedProvider();
      service.registerFeedProvider(smhProvider);
      
      const theAgeProvider = new TheAgeFeedProvider();
      service.registerFeedProvider(theAgeProvider);
      
      const nineNewsProvider = new NineNewsFeedProvider();
      service.registerFeedProvider(nineNewsProvider);
      
      // SBS News (Special Broadcasting Service)
      const sbsProvider = new SBSNewsFeedProvider();
      service.registerFeedProvider(sbsProvider);
      
      // News Corp Australia - DISABLED: Legal issues with commercial use
      // const newsComAuProvider = new NewsComAuFeedProvider();
      // service.registerFeedProvider(newsComAuProvider);
      
      console.log('✅ Registered 6 media feed providers (1 Google + 5 Australian sources)');
      
      // Register enhanced processors
      const basicProcessor = new BasicArticleProcessor();
      service.registerProcessor(basicProcessor);
      
      setMediaService(service);
      
      // IMMEDIATELY SET METRICS TO SHOW ACTIVE STATUS
      const initialMetrics = service.getMetrics();
      console.log('📊 Initial metrics after service creation:', initialMetrics);
      setMetrics(initialMetrics);
      
      // SET ORGANIZATION ID for system chat messages
      if (currentUser?.orgId) {
        service.setCurrentOrgId(currentUser.orgId);
      }
      
      console.log('✅ Enhanced Media Intelligence Service initialized');
      
    } catch (error) {
      console.error('❌ Error initializing Media Intelligence:', error);
      setError('Failed to initialize media intelligence service');
    }
  }, [db, currentUser?.orgId]); // Added currentUser.orgId to prevent unnecessary re-initialization

  // Load articles and set up real-time monitoring
  useEffect(() => {
    if (!mediaService || !currentUser?.orgId || !incidentId) return;

    const loadMediaData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('📊 Loading media data for incident:', incidentId);

        // IMMEDIATE: Purge ALL demo articles from database FIRST
        console.log('🧹 EMERGENCY CLEANUP: Purging ALL demo articles from database...');
        await purgeAllDemoArticlesFromDatabase();

        // ACKNOWLEDGE high-relevance articles (clear badge counter)
        await acknowledgeHighRelevanceArticles();

        // STEP 1: Fetch the actual organization data
              const orgData = await fetchOrganisationData(currentUser.orgId);
      console.log('🏢 Organisation Data:', orgData);
      setOrganisationData(orgData);

        // STEP 2: Fetch the actual incident/crisis data
        const crisisData = await fetchCrisisData(incidentId);
        console.log('🚨 Crisis Data:', crisisData);

        // Get existing articles (with error handling)
        let existingArticles = [];
        try {
          existingArticles = await mediaService.getArticlesForOrg(currentUser.orgId, {
            limit: 100  // INCREASED: Get more articles to create more story threads
          });
          console.log(`📰 Loaded ${existingArticles.length} existing articles`);
        } catch (articleError) {
          console.warn('⚠️ Failed to load existing articles:', articleError.message);
          // Continue with empty array
        }
        
        // 🎯 SORT BY RELEVANCE: Highest relevance articles at the top!
        const sortedArticles = sortArticlesByRelevance(existingArticles);
        console.log('🔝 Articles sorted by relevance - highest relevance first');
        setArticles(sortedArticles);
        
        // Get service metrics
        const serviceMetrics = mediaService.getMetrics();
        setMetrics(serviceMetrics);

        // Set up real-time subscription (with error handling)
        let unsubscribe = () => {};
        try {
          unsubscribe = mediaService.subscribeToArticles(currentUser.orgId, (newArticles) => {
            // 🎯 SORT BY RELEVANCE: Highest relevance articles at the top!
            const sortedNewArticles = sortArticlesByRelevance(newArticles);
            console.log(`📊 Received ${newArticles.length} articles from real-time feed - sorted by relevance`);
            setArticles(sortedNewArticles);
          });
        } catch (subscriptionError) {
          console.warn('⚠️ Failed to set up real-time subscription:', subscriptionError.message);
        }

        // Start monitoring (if not already running)
        if (!serviceMetrics.isRunning) {
          console.log('🎬 Starting CRISIS-SPECIFIC media monitoring...');
          
          try {
            // Create monitoring campaign based on ACTUAL CRISIS DATA
            await createCrisisSpecificMonitoringCampaign(orgData, crisisData);
            
            // Start monitoring with CRISIS-SPECIFIC keywords
            await startCrisisSpecificMonitoring(orgData, crisisData);
          } catch (monitoringError) {
            console.warn('⚠️ Failed to start monitoring:', monitoringError.message);
            // Show warning but don't crash
            setError('Media monitoring failed to start, but existing articles are available');
          }
        }

        setLoading(false);
        return unsubscribe;

      } catch (error) {
        console.error('❌ Error loading media data:', error);
        setError('Failed to load media intelligence. Please try refreshing the page.');
        setLoading(false);
      }
    };

    loadMediaData().catch(error => {
      console.error('❌ Unhandled error in loadMediaData:', error);
      setError('Failed to initialize media intelligence');
      setLoading(false);
    });

    // Cleanup function will be handled by the component unmount
    return () => {
      try {
        if (mediaService) {
          mediaService.stop();
        }
      } catch (cleanupError) {
        console.warn('⚠️ Error during cleanup:', cleanupError.message);
      }
    };
  }, [mediaService, currentUser?.orgId, incidentId]);

  /**
   * ACKNOWLEDGE high-relevance articles (clear badge counter)
   */
  const acknowledgeHighRelevanceArticles = async () => {
    if (!db || !currentUser?.id) return;

    try {
      console.log('👁️ Acknowledging high-relevance articles to clear badge...');

      // Get all high-relevance articles from last 24 hours
      const articlesRef = collection(db, 'c3_media_articles');
      const snapshot = await getDocs(articlesRef);
      
      const now = new Date();
      const last24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      
      let acknowledgedCount = 0;
      
      for (const docSnapshot of snapshot.docs) {
        const article = docSnapshot.data();
        const createdAt = article.createdAt ? 
          (article.createdAt.toDate ? article.createdAt.toDate() : new Date(article.createdAt)) : 
          new Date();
        
        const shouldAcknowledge = (
          article.relevanceScore >= 0.95 && // High relevance
          createdAt >= last24Hours && // Created in last 24 hours
          !article.acknowledgedBy?.includes(currentUser.id) // Not yet acknowledged
        );
        
        if (shouldAcknowledge) {
          try {
            const articleRef = doc(db, 'c3_media_articles', docSnapshot.id);
            const acknowledgedBy = article.acknowledgedBy || [];
            
            await updateDoc(articleRef, {
              acknowledgedBy: [...acknowledgedBy, currentUser.id],
              acknowledgedAt: serverTimestamp()
            });
            
            acknowledgedCount++;
          } catch (updateError) {
            console.error('❌ Error acknowledging article:', updateError);
          }
        }
      }
      
      console.log(`✅ Acknowledged ${acknowledgedCount} high-relevance articles`);
      
    } catch (error) {
      console.error('❌ Error acknowledging articles:', error);
    }
  };

  /**
   * EMERGENCY: Purge ALL demo articles AND irrelevant content from database immediately
   * ✅ RE-ENABLED: Firestore rules updated to allow authenticated users to delete
   */
  const purgeAllDemoArticlesFromDatabase = async () => {
    if (!db) return;

    try {
      console.log('🚨 EMERGENCY PURGE: Finding and deleting ALL demo articles AND irrelevant content...');

      // Get ALL articles from database (increase limit to catch everything)
      const articlesRef = collection(db, 'c3_media_articles');
      const snapshot = await getDocs(articlesRef);
      
      let deletedCount = 0;
      let totalCount = snapshot.size;
      
      console.log(`📄 Scanning ${totalCount} articles for demo AND irrelevant content...`);
      
      for (const docSnapshot of snapshot.docs) {
        const article = docSnapshot.data();
        
        // Identify ANY demo-related content OR irrelevant content
        const shouldDelete = 
          // DEMO ARTICLES
          article.isDemoArticle ||
          (article.feedSource && (
            article.feedSource.includes('Demo:') ||
            article.feedSource.includes('DEMO') ||
            article.feedSource.includes('demo') ||
            article.feedSource.includes('Demo - Crisis-Specific') ||
            article.feedSource.includes('Demo: Crisis-Specific')
          )) ||
          (article.url && article.url.includes('demo.crisis-system.com')) ||
          (article.title && (
            article.title.includes('Medical Emergency Response Team Deployed to Melbourne Office Building') ||
            article.title.includes('Ambulance Services Respond to Medical Incident at Exhibition Street Office') ||
            article.title.includes('Medical Emergency Response Team Deployed') ||
            article.title.includes('Ambulance Services Respond to Medical Incident')
          )) ||
          // IRRELEVANT US/UK/GENERIC CONTENT
          (article.title && (
            article.title.includes('Jackson Outlet Mall') ||
            article.title.includes('Rutherford County') ||
            article.title.includes('York County') ||
            article.title.includes('Lancaster grocery store') ||
            article.title.includes('Colorado mountain town') ||
            article.title.includes('Milton Keynes') ||
            article.title.includes('Hutchinson area') ||
            article.title.includes('Waterford Penney') ||
            article.title.toLowerCase().includes('giant grocery store') ||
            article.title.toLowerCase().includes('lowe\'s') ||
            article.title.toLowerCase().includes('patch') ||
            article.title.toLowerCase().includes('wyff') ||
            article.title.toLowerCase().includes('abc27') ||
            article.title.toLowerCase().includes('wgal') ||
            article.title.toLowerCase().includes('now habersham') ||
            article.title.toLowerCase().includes('cbs news') ||
            article.title.toLowerCase().includes('mkfm') ||
            article.title.toLowerCase().includes('wichita eagle') ||
            article.title.toLowerCase().includes('waterford-news.ie')
          )) ||
          // GENERIC SEARCH TERMS (NOT ORGANIZATION-SPECIFIC)
          (article.feedSource && article.feedSource.includes('store evacuation')) ||
          (article.feedSource && !article.feedSource.toLowerCase().includes('fifteen lashes brewery')) ||
          // LOW RELEVANCE SCORES
          (article.relevanceScore && article.relevanceScore < 0.95) ||
          // DOESN'T MENTION ORGANIZATION
          (!article.title?.toLowerCase().includes('fifteen lashes brewery') &&
           !article.content?.toLowerCase().includes('fifteen lashes brewery'));
        
        if (shouldDelete) {
          try {
            await deleteDoc(doc(db, 'c3_media_articles', docSnapshot.id));
            deletedCount++;
            
            let deleteReason = 'Unknown';
            if (article.isDemoArticle) deleteReason = 'DEMO ARTICLE';
            else if (article.title?.includes('Jackson Outlet Mall')) deleteReason = 'US CONTENT';
            else if (article.feedSource?.includes('store evacuation')) deleteReason = 'GENERIC SEARCH';
            else if (article.relevanceScore < 0.95) deleteReason = 'LOW RELEVANCE';
            else if (!article.title?.toLowerCase().includes('fifteen lashes brewery')) deleteReason = 'NOT ORGANIZATION-SPECIFIC';
            else deleteReason = 'IRRELEVANT CONTENT';
            
            console.log(`🗑️ PURGED (${deleteReason}): ${article.title?.substring(0, 50)}...`);
          } catch (deleteError) {
            console.error('Error deleting irrelevant article:', deleteError);
          }
        }
      }
      
      console.log(`✅ AGGRESSIVE PURGE COMPLETE: ${deletedCount} irrelevant articles PERMANENTLY DELETED from database`);
      console.log(`📊 ${totalCount - deletedCount} relevant articles remain`);
      
    } catch (error) {
      console.error('❌ Error during emergency purge:', error);
    }
  };

  /**
   * Fetch actual organisation data from Firestore
   */
  const fetchOrganisationData = async (orgId) => {
    try {
      const orgRef = doc(db, 'c3_organisations', orgId);
      const orgDoc = await getDoc(orgRef);
      
      if (orgDoc.exists()) {
        const data = orgDoc.data();
        console.log('📋 Raw org data:', data);
        
        return {
          id: orgDoc.id,
          name: data.name || data.companyName || 'Unknown Organisation',
          companyName: data.companyName,
          primaryAddress: data.primaryAddress,
          additionalLocations: data.additionalLocations || [],
          industry: data.industry,
          abn: data.abn
        };
      } else {
        console.warn('❌ Organisation not found:', orgId);
        return {
          id: orgId,
          name: 'Unknown Organisation',
          primaryAddress: null,
          additionalLocations: [],
          industry: null
        };
      }
    } catch (error) {
      console.error('❌ Error fetching organisation data:', error);
      return {
        id: orgId,
        name: 'Error Loading Organisation',
        primaryAddress: null,
        additionalLocations: [],
        industry: null
      };
    }
  };

  /**
   * Fetch actual crisis/incident data from Firestore
   */
  const fetchCrisisData = async (incidentId) => {
    try {
      const incidentRef = doc(db, 'c3_incidents', incidentId);
      const incidentDoc = await getDoc(incidentRef);
      
      if (incidentDoc.exists()) {
        const data = incidentDoc.data();
        console.log('📋 Raw crisis data:', data);
        
        return {
          id: incidentDoc.id,
          crisisType: data.crisisType || data.type || 'Unknown Crisis',
          location: data.location,
          severity: data.severity,
          description: data.description,
          affectedSystems: data.affectedSystems || [],
          keywords: data.keywords || [],
          createdAt: data.createdAt,
          status: data.status
        };
      } else {
        console.warn('❌ Incident not found:', incidentId);
        return {
          id: incidentId,
          crisisType: 'Unknown Crisis',
          location: null,
          severity: 'unknown',
          description: null,
          affectedSystems: [],
          keywords: []
        };
      }
    } catch (error) {
      console.error('❌ Error fetching crisis data:', error);
      return {
        id: incidentId,
        crisisType: 'Error Loading Crisis',
        location: null,
        severity: 'unknown',
        description: null,
        affectedSystems: [],
        keywords: []
      };
    }
  };

  /**
   * Create monitoring campaign based on ACTUAL CRISIS DATA
   */
  const createCrisisSpecificMonitoringCampaign = async (orgData, crisisData) => {
    try {
      const campaignData = {
        orgId: currentUser.orgId,
        incidentId: incidentId,
        isActive: true,
        alertRules: {
          organizationMentions: [orgData.name, orgData.companyName].filter(Boolean),
          keywords: buildCrisisSpecificSearchTerms(orgData, crisisData),
          locations: extractCrisisLocations(orgData, crisisData),
          crisisType: crisisData.crisisType,
          severity: crisisData.severity
        },
        createdAt: new Date()
      };

      console.log('📋 Created CRISIS-SPECIFIC monitoring campaign:', campaignData);
      return campaignData;

    } catch (error) {
      console.error('❌ Error creating crisis-specific monitoring campaign:', error);
    }
  };

  /**
   * Build crisis-specific search terms using GPT expansion (NEW APPROACH)
   */
  const buildCrisisSpecificSearchTermsAsync = async (orgData, crisisData, liveMessages = []) => {
    try {
      console.log('🤖 Building GPT-EXPANDED search terms...');
      
      // Use GPT to expand keywords dynamically
      const expandedKeywords = await gptKeywordService.expandKeywords(crisisData, orgData, liveMessages);
      
      // Store expanded keywords for display/debugging
      setExpandedKeywords(expandedKeywords);
      
      console.log(`✅ GPT expanded ${expandedKeywords.length} keywords:`, expandedKeywords);
      
      // Build search terms using expanded keywords
      const searchTerms = [];
      const companyNames = [orgData.name, orgData.companyName].filter(Boolean);
      
      // Combine organization names with expanded keywords
      companyNames.forEach(company => {
        if (company && company.trim().length > 2) {
          // Core company + keyword combinations (priority terms)
          expandedKeywords.slice(0, 8).forEach(keyword => {
            if (keyword && keyword.length > 2) {
              searchTerms.push(`"${company.trim()}" "${keyword}"`);
            }
          });
          
          // Company + emergency terms
          searchTerms.push(`"${company.trim()}" emergency`);
          searchTerms.push(`"${company.trim()}" incident`);
          searchTerms.push(`"${company.trim()}" crisis`);
        }
      });
      
      // Add pure keyword searches (without company) for broader coverage
      expandedKeywords.slice(0, 12).forEach(keyword => {
        if (keyword && keyword.length > 2) {
          searchTerms.push(keyword);
        }
      });
      
      console.log('🎯 Generated GPT-EXPANDED search terms:', searchTerms);
      return [...new Set(searchTerms)]; // Remove duplicates
      
    } catch (error) {
      console.error('❌ GPT keyword expansion failed, using fallback:', error);
      return buildFallbackSearchTerms(orgData, crisisData);
    }
  };

  /**
   * Fallback search terms if GPT expansion fails (LEGACY APPROACH)
   */
  const buildFallbackSearchTerms = (orgData, crisisData) => {
    const searchTerms = [];
    const companyNames = [orgData.name, orgData.companyName].filter(Boolean);
    const crisisType = crisisData.crisisType;
    const locations = extractCrisisLocations(orgData, crisisData);
    
    companyNames.forEach(company => {
      if (company && company.trim().length > 2) {
        if (crisisType && crisisType !== 'Unknown Crisis') {
          searchTerms.push(`"${company.trim()}" "${crisisType}"`);
        }
        
        locations.forEach(location => {
          if (location && location.trim().length > 2) {
            searchTerms.push(`"${company.trim()}" "${location.trim()}"`);
          }
        });
        
        searchTerms.push(`"${company.trim()}" emergency`);
        searchTerms.push(`"${company.trim()}" incident`);
        searchTerms.push(`"${company.trim()}" crisis`);
      }
    });
    
    return [...new Set(searchTerms)];
  };

  /**
   * Synchronous version for backward compatibility
   */
  const buildCrisisSpecificSearchTerms = (orgData, crisisData) => {
    // Return expanded keywords if available, otherwise use fallback
    if (expandedKeywords.length > 0) {
      const searchTerms = [];
      const companyNames = [orgData.name, orgData.companyName].filter(Boolean);
      
      companyNames.forEach(company => {
        if (company && company.trim().length > 2) {
          expandedKeywords.slice(0, 8).forEach(keyword => {
            if (keyword && keyword.length > 2) {
              searchTerms.push(`"${company.trim()}" "${keyword}"`);
            }
          });
        }
      });
      
      expandedKeywords.slice(0, 12).forEach(keyword => {
        if (keyword && keyword.length > 2) {
          searchTerms.push(keyword);
        }
      });
      
      return [...new Set(searchTerms)];
    }
    
    return buildFallbackSearchTerms(orgData, crisisData);
  };

  /**
   * Get crisis-type-specific search terms
   */
  const getCrisisTypeSpecificTerms = (crisisType) => {
    const crisisTypeMap = {
      'Medical Emergency': ['medical emergency', 'ambulance', 'paramedic', 'hospital', 'medical incident'],
      'Fire': ['fire', 'blaze', 'smoke', 'evacuation', 'fire department'],
      'Cyber Attack': ['cyber attack', 'data breach', 'hack', 'security breach', 'ransomware'],
      'Flood': ['flood', 'flooding', 'water damage', 'storm', 'rainfall'],
      'Workplace Accident': ['workplace accident', 'injury', 'safety incident', 'worker injured'],
      'Power Outage': ['power outage', 'blackout', 'electricity', 'power failure'],
      'Gas Leak': ['gas leak', 'gas emergency', 'evacuation', 'hazmat'],
      'Active Threat': ['active threat', 'lockdown', 'security alert', 'police response'],
      'Chemical Spill': ['chemical spill', 'hazmat', 'evacuation', 'environmental']
    };

    const normalizedCrisisType = crisisType?.trim();
    return crisisTypeMap[normalizedCrisisType] || [];
  };

  /**
   * Extract locations from both organization and crisis data
   */
  const extractCrisisLocations = (orgData, crisisData) => {
    const locations = [];

    // Add crisis-specific location if available
    if (crisisData.location) {
      locations.push(crisisData.location);
    }

    // Add organization locations
    if (orgData.primaryAddress) {
      const addressParts = orgData.primaryAddress.split(',');
      addressParts.forEach(part => {
        const trimmed = part.trim();
        // Look for city/suburb patterns (not postcodes or states)
        if (trimmed.match(/^[A-Za-z\s]+$/) && trimmed.length > 3 && !trimmed.match(/NSW|VIC|QLD|WA|SA|NT|ACT|TAS/i)) {
          locations.push(trimmed);
        }
      });
    }

    // Add additional org locations
    if (orgData.additionalLocations && Array.isArray(orgData.additionalLocations)) {
      orgData.additionalLocations.forEach(location => {
        if (location.address) {
          const addressParts = location.address.split(',');
          addressParts.forEach(part => {
            const trimmed = part.trim();
            if (trimmed.match(/^[A-Za-z\s]+$/) && trimmed.length > 3 && !trimmed.match(/NSW|VIC|QLD|WA|SA|NT|ACT|TAS/i)) {
              locations.push(trimmed);
            }
          });
        }
      });
    }

    // Remove duplicates and return
    const uniqueLocations = [...new Set(locations)];
    console.log('📍 Extracted CRISIS-SPECIFIC locations:', uniqueLocations);
    return uniqueLocations;
  };

  /**
   * Start crisis-specific media monitoring
   */
  const startCrisisSpecificMonitoring = async (orgData, crisisData) => {
    if (!mediaService) {
      throw new Error('Media service not available');
    }

    try {
      // Start monitoring with crisis-specific parameters
      const monitoringOptions = {
        keywords: buildCrisisSpecificSearchTerms(orgData, crisisData),
        locations: extractCrisisLocations(orgData, crisisData),
        organisations: [orgData.name, orgData.companyName].filter(Boolean),
        crisisType: crisisData.crisisType,
        severity: crisisData.severity,
        orgId: currentUser.orgId // 🔧 FIX: Pass orgId for proper article storage
      };

      console.log('🚀 Starting crisis-specific monitoring with:', monitoringOptions);

      // Start monitoring cycle with longer timeout for multiple RSS feeds
      await Promise.race([
        mediaService.runMonitoringCycle(monitoringOptions),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Monitoring cycle timeout')), 60000) // Increased to 60s for multiple feeds
        )
      ]);

      console.log('✅ Crisis-specific monitoring started successfully');
      
      // Update metrics AND refresh articles immediately
      const serviceMetrics = mediaService.getMetrics();
      setMetrics(serviceMetrics);

      // 🔧 FIX: Wait for Firestore eventual consistency before retrieval
      console.log('⏳ Waiting 3s for database consistency...');
      await new Promise(resolve => setTimeout(resolve, 3000));

      // FORCE REFRESH: Get updated articles after monitoring cycle
      try {
        const updatedArticles = await mediaService.getArticlesForOrg(currentUser.orgId, {
          limit: 100  // INCREASED: Get more articles to create more story threads
        });
        console.log(`🔄 Refreshed articles after monitoring: ${updatedArticles.length} articles`);
        // 🎯 SORT BY RELEVANCE: Highest relevance articles at the top!
        const sortedUpdatedArticles = sortArticlesByRelevance(updatedArticles);
        setArticles(sortedUpdatedArticles);
      } catch (articleRefreshError) {
        console.warn('⚠️ Failed to refresh articles after monitoring:', articleRefreshError.message);
      }

    } catch (error) {
      console.error('❌ Error starting crisis-specific monitoring:', error);
      
      // Only show error for actual failures, not timeouts when system is working
      if (error.message === 'Monitoring cycle timeout') {
        console.warn('⏰ Monitoring cycle took longer than expected, but system is working correctly');
        // Don't set error state for timeouts - the system is actually working fine
        // setError('Media monitoring is slow to start but will continue in background');
      } else {
        // Only set error for real failures
        setError('Media monitoring failed to start, but existing articles are available');
      }
      
      // Still update metrics even on timeout
      try {
        const serviceMetrics = mediaService.getMetrics();
        setMetrics(serviceMetrics);
      } catch (metricsError) {
        console.warn('Could not get service metrics:', metricsError);
      }
    }
  };

  /**
   * Get sentiment color
   */
  const getSentimentColor = (sentiment) => {
    if (!sentiment) return 'default';
    
    switch (sentiment.label) {
      case 'positive': return 'green';
      case 'negative': return 'red';
      case 'neutral': return 'blue';
      default: return 'default';
    }
  };

  /**
   * Get relevance color
   */
  const getRelevanceColor = (score) => {
    if (score >= 0.7) return 'red';
    if (score >= 0.4) return 'orange';
    return 'green';
  };

  /**
   * Format time ago - FIXED TO AVOID NaN
   */
  const formatTimeAgo = (date) => {
    if (!date) return 'Unknown time';
    
    try {
      const now = new Date();
      let publishedDate;
      
      // Handle different date formats
      if (date.toDate && typeof date.toDate === 'function') {
        // Firebase Timestamp
        publishedDate = date.toDate();
      } else if (typeof date === 'string' || typeof date === 'number') {
        publishedDate = new Date(date);
      } else if (date instanceof Date) {
        publishedDate = date;
      } else {
        console.warn('Unknown date format:', date);
        return 'Unknown time';
      }
      
      // Check if date is valid
      if (isNaN(publishedDate.getTime())) {
        console.warn('Invalid date:', date);
        return 'Invalid time';
      }
      
      const diffMs = now - publishedDate;
      
      // Handle future dates (clock sync issues)
      if (diffMs < 0) {
        return 'Just now';
      }
      
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffDays > 0) {
        return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      } else if (diffHours > 0) {
        return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      } else if (diffMinutes > 0) {
        return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
      } else {
        return 'Just now';
      }
    } catch (error) {
      console.error('Error formatting time:', error, date);
      return 'Unknown time';
    }
  };

  /**
   * Handle filter sensitivity change - ACTUALLY TRIGGER NEW SEARCH
   */
  const handleFilterSensitivityChange = async (value) => {
    setFilterSensitivity(value);
    if (mediaService) {
      console.log(`🎚️ FILTER SENSITIVITY CHANGED TO: ${value} - TRIGGERING NEW SEARCH...`);
      
      // Update service configuration
      mediaService.setFilteringSensitivity(value);
      
      // IMMEDIATELY TRIGGER NEW SEARCH WITH NEW SENSITIVITY
      try {
        setLoading(true);
        setArticles([]); // Clear current articles
        
        // Get org and crisis data for search
        const orgData = await fetchOrganisationData(currentUser.orgId);
        const crisisData = await fetchCrisisData(incidentId);
        
        // Run new monitoring cycle with updated sensitivity
        const monitoringOptions = {
          keywords: buildCrisisSpecificSearchTerms(orgData, crisisData),
          locations: extractCrisisLocations(orgData, crisisData),
          organisations: [orgData.name, orgData.companyName].filter(Boolean),
          crisisType: crisisData.crisisType,
          severity: crisisData.severity,
          includeCrisisKeywords: false,
          orgId: currentUser.orgId // 🔧 FIX: Pass orgId for proper article storage
        };
        
        console.log(`🔄 Re-running search with ${value} sensitivity...`);
        await mediaService.runMonitoringCycle(monitoringOptions);
        
        // 🔧 FIX: Wait for Firestore eventual consistency before retrieval
        console.log('⏳ Waiting 3s for database consistency...');
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        // Get fresh articles with new filtering
        const freshArticles = await mediaService.getArticlesForOrg(currentUser.orgId, {
          limit: 100  // INCREASED: Get more articles to create more story threads
        });
        
        // 🎯 SORT BY RELEVANCE: Highest relevance articles at the top!
        const sortedFreshArticles = sortArticlesByRelevance(freshArticles);
        setArticles(sortedFreshArticles);
        setMetrics(mediaService.getMetrics());
        setLoading(false);
        
        console.log(`✅ Filter sensitivity change complete: ${freshArticles.length} articles with ${value} filtering`);
        
      } catch (error) {
        console.error('❌ Error updating filter sensitivity:', error);
        setLoading(false);
      }
    }
  };

  /**
   * Toggle filtered articles display
   */
  const handleShowFilteredToggle = (checked) => {
    setShowFilteredArticles(checked);
    if (mediaService) {
      mediaService.updateConfig({ storeFilteredArticles: checked });
    }
  };

  /**
   * Get content type color for display
   */
  const getContentTypeColor = (contentType) => {
    const colors = {
      'breaking_news': 'red',
      'crisis_news': 'orange',
      'general_news': 'blue',
      'podcast': 'purple',
      'entertainment': 'pink',
      'sports': 'green',
      'filtered': 'default'
    };
    return colors[contentType] || 'default';
  };

  /**
   * Get source credibility badge
   */
  const getSourceCredibilityBadge = (credibility) => {
    const badges = {
      'high': { color: 'green', text: 'High Credibility' },
      'medium': { color: 'blue', text: 'Medium Credibility' },
      'low': { color: 'red', text: 'Low Credibility' }
    };
    return badges[credibility] || { color: 'default', text: 'Unknown' };
  };

  /**
   * Clean up irrelevant articles from the database
   */
  const handleCleanupIrrelevantArticles = async () => {
    if (!mediaService || !currentUser?.orgId) return;

    try {
      console.log('🧹 AGGRESSIVE CLEANUP: Removing ALL DEMO ARTICLES AND IRRELEVANT CONTENT...');

      // Use the same emergency purge logic
      await purgeAllDemoArticlesFromDatabase();
      
    } catch (error) {
      console.error('❌ Error during aggressive cleanup:', error);
      // Don't throw the error - just log it and continue
    }
  };

  /**
   * TEST: Direct local proxy connectivity
   */
  const testLocalProxy = async () => {
    console.log('🧪 TESTING LOCAL PROXY CONNECTIVITY...');
    
    try {
      // Test 1: Basic health check
      console.log('🔍 Test 1: Health check...');
      const rssProxyUrl = process.env.REACT_APP_RSS_PROXY_URL || 'https://australia-southeast2-crisiscompass-prod.cloudfunctions.net/rssProxy';
      const healthResponse = await fetch(rssProxyUrl);
      console.log('✅ Health check response:', healthResponse.status, await healthResponse.text());
      
      // Test 2: Simple RSS proxy call
      console.log('🔍 Test 2: Simple RSS proxy...');
      const testUrl = 'https://news.google.com/rss?hl=en-AU&gl=AU&ceid=AU:en';
      const proxyUrl = `${rssProxyUrl}?url=${encodeURIComponent(testUrl)}`;
      console.log('🔗 Testing proxy URL:', proxyUrl);
      
      const proxyResponse = await fetch(proxyUrl);
      console.log('✅ Proxy response:', proxyResponse.status, proxyResponse.statusText);
      
      if (proxyResponse.ok) {
        const data = await proxyResponse.text();
        console.log('✅ Proxy returned:', data.length, 'bytes');
        return true;
      } else {
        console.error('❌ Proxy failed:', proxyResponse.status, proxyResponse.statusText);
        return false;
      }
      
    } catch (error) {
      console.error('❌ Local proxy test failed:', error);
      return false;
    }
  };

  /**
   * Manual refresh of articles with CRISIS-SPECIFIC keywords
   */
  const handleRefresh = async () => {
    if (!mediaService || !incidentId) return;

    try {
      setLoading(true);
      console.log('🔄 MANUAL REFRESH STARTED - CRISIS-SPECIFIC SEARCH ONLY...');
      
      // FIRST: Clear existing articles from UI immediately
      setArticles([]);
      setError(null);
      
      // FORCE MONITORING STATUS TO ACTIVE IMMEDIATELY
      const initialMetrics = mediaService.getMetrics();
      initialMetrics.isRunning = true;
      initialMetrics.lastRunTime = Date.now();
      setMetrics(initialMetrics);
      
      // SECOND: AGGRESSIVE CLEANUP - Remove ALL irrelevant articles immediately
      console.log('🧹 STARTING AGGRESSIVE CLEANUP OF CELEBRITY/ENTERTAINMENT TRASH...');
      await handleCleanupIrrelevantArticles();
      
      // THIRD: Fetch organisation and crisis data for targeted search
      const orgData = await fetchOrganisationData(currentUser.orgId);
      const crisisData = await fetchCrisisData(incidentId);
      
      // NEW: Get live messages for keyword expansion context
      const liveMessages = []; // TODO: Fetch actual live messages from the incident
      
      // FOURTH: Get GPT-EXPANDED search terms 
      console.log('🤖 Generating GPT-expanded keywords...');
      const expandedSearchTerms = await buildCrisisSpecificSearchTermsAsync(orgData, crisisData, liveMessages);
      
      const monitoringOptions = {
        keywords: expandedSearchTerms,
        locations: extractCrisisLocations(orgData, crisisData),
        organisations: [orgData.name, orgData.companyName].filter(Boolean),
        crisisType: crisisData.crisisType,
        severity: crisisData.severity,
        includeCrisisKeywords: false, // DISABLE generic keywords - using GPT expansion instead
        forceRun: true, // 🚨 FORCE RUN for manual refresh - bypass cost optimization
        orgId: currentUser.orgId // 🔧 FIX: Pass orgId for proper article storage
      };

      console.log('🔄 Refreshing with CRISIS-SPECIFIC monitoring options:', monitoringOptions);
      
      // FIFTH: Run monitoring cycle with CRISIS-SPECIFIC keywords only
      await mediaService.runMonitoringCycle(monitoringOptions);
      
      // 🔧 FIX: Wait for Firestore eventual consistency before retrieval
      console.log('⏳ Waiting 3s for database consistency...');
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // SIXTH: Get fresh articles and update UI
      const freshArticles = await mediaService.getArticlesForOrg(currentUser.orgId, {
        limit: 100  // INCREASED: Get more articles to create more story threads
      });
      console.log(`✅ REFRESH COMPLETE: ${freshArticles.length} CRISIS-RELEVANT articles loaded (could be 0 if no matches found)`);
      
      // 🎯 SORT BY RELEVANCE: Highest relevance articles at the top!
      const sortedFreshArticles = sortArticlesByRelevance(freshArticles);
      setArticles(sortedFreshArticles);
      
      // SEVENTH: Get search metadata from ALL providers to show what sources were checked
      try {
        const allSearchSources = [];
        
        // Collect metadata from all registered providers
        for (const provider of mediaService.feedProviders || []) {
          if (provider.getLastSearchMetadata) {
            const metadata = provider.getLastSearchMetadata();
            if (metadata && metadata.searchedSources) {
              // Add provider name to each source for identification
              const sourcesWithProvider = metadata.searchedSources.map(source => ({
                ...source,
                provider: provider.name
              }));
              allSearchSources.push(...sourcesWithProvider);
            }
          }
        }
        
        console.log('📋 All provider search metadata:', allSearchSources);
        setLastSearchSources(allSearchSources);
        
      } catch (metadataError) {
        console.warn('⚠️ Could not get search metadata:', metadataError);
        setLastSearchSources([]);
      }
      
      // EIGHTH: Update metrics and force monitoring status to active
      const finalMetrics = mediaService.getMetrics();
      finalMetrics.isRunning = true; // Force status to show active
      finalMetrics.lastRunTime = Date.now(); // Update last run time
      setMetrics(finalMetrics);
      
      setLoading(false);
    } catch (error) {
      console.error('❌ Error refreshing articles:', error);
      setError('Failed to refresh articles');
      setLoading(false);
    }
  };

  /**
   * DEMO: Add a sample system message to show how alerts appear
   */
  const addDemoSystemMessage = async () => {
    if (!db || !currentUser?.orgId || !incidentId) {
      message.error('Cannot add demo message - missing required data');
      return;
    }

    try {
      console.log('🎯 Adding demo system message...');

      // Create the demo system message
      const systemMessage = `🚨 **HIGH-RELEVANCE MEDIA ALERT** (96%)

**Article:** Emergency Response Team Deployed to Fifteen Lashes Brewery Following Workplace Incident
**Source:** ABC News Australia  
**Published:** 23 minutes ago

**Crisis Indicators:** Medical Emergency, Workplace Safety, Emergency Response

**Link:** https://www.abc.net.au/news/2024-01-15/brewery-workplace-incident/102345678

*This article was automatically detected by the Media Intelligence system due to its high relevance to your organization.*`;

      // Add the system message to chat
      const chatRef = collection(db, 'c3_incidentChats');
      await addDoc(chatRef, {
        incidentId: incidentId,
        message: systemMessage,
        timestamp: serverTimestamp(),
        authorId: 'system',
        authorName: 'Media Intelligence System',
        firstName: 'Media Intelligence',
        lastName: 'System', 
        role: 'system',
        orgId: currentUser.orgId,
        isSystemMessage: true,
        mediaArticleId: 'demo_article_' + Date.now(),
        articleRelevanceScore: 0.96
      });

      message.success('Demo system message added to live chat!');
      console.log('✅ Demo system message added successfully!');
      
    } catch (error) {
      console.error('❌ Error adding demo system message:', error);
      message.error('Failed to add demo system message');
    }
  };

  /**
   * Toggle cluster expansion state
   */
  const toggleClusterExpansion = (clusterId) => {
    setExpandedClusters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(clusterId)) {
        newSet.delete(clusterId);
      } else {
        newSet.add(clusterId);
      }
      return newSet;
    });
  };

  /**
   * Render a clustered article item with expandable supporting articles
   */
  const renderClusteredItem = (cluster) => {
    if (cluster.type === 'standalone') {
      // Render as regular article
      return renderArticleItem(cluster);
    }

    const { primaryArticle, supportingArticles, totalSources, confidence, sources } = cluster;
    const expanded = expandedClusters.has(cluster.id);

    return (
      <List.Item
        key={cluster.id}
        style={{
          border: '1px solid #d9d9d9',
          borderRadius: '8px',
          marginBottom: '12px',
          padding: '16px',
          backgroundColor: '#fafafa'
        }}
        actions={[
          <Tooltip title="View primary article">
            <Button 
              type="link" 
              icon={<LinkOutlined />}
              href={primaryArticle.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Read Primary
            </Button>
          </Tooltip>,
          supportingArticles.length > 0 && (
            <Button 
              type="text" 
              size="small"
              onClick={() => toggleClusterExpansion(cluster.id)}
              icon={expanded ? <EyeOutlined /> : <EyeOutlined />}
            >
              {expanded ? 'Hide' : 'Show'} {supportingArticles.length} more source{supportingArticles.length > 1 ? 's' : ''}
            </Button>
          )
        ].filter(Boolean)}
      >
        <List.Item.Meta
          avatar={
            <div style={{ textAlign: 'center' }}>
              {/* Cluster indicator */}
              <div style={{ 
                backgroundColor: confidence === 'HIGH' ? '#52c41a' : '#1890ff',
                color: 'white',
                borderRadius: '50%',
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 'bold',
                marginBottom: 4
              }}>
                {totalSources}
              </div>
              {primaryArticle.crisisIndicators?.length > 0 && (
                <AlertOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
              )}
              {primaryArticle.relevanceScore >= 0.7 && (
                <FireOutlined style={{ color: '#fa8c16', fontSize: 16 }} />
              )}
            </div>
          }
          title={
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Space>
                <Text strong style={{ fontSize: 16 }}>
                  {primaryArticle.title}
                </Text>
                <Tag color={confidence === 'HIGH' ? 'green' : 'blue'} style={{ fontSize: 10 }}>
                  {confidence} CONFIDENCE
                </Tag>
              </Space>
              <Space wrap>
                {/* Source cluster display */}
                <Tag color="purple" icon={<GlobalOutlined />}>
                  {totalSources} source{totalSources > 1 ? 's' : ''}: {sources.slice(0, 2).join(', ')}
                  {sources.length > 2 && ` +${sources.length - 2} more`}
                </Tag>
                {primaryArticle.feedSource && (
                  <Tag color="cyan">
                    Primary: {primaryArticle.feedSource}
                  </Tag>
                )}
                {primaryArticle.contentType && (
                  <Tag color={getContentTypeColor(primaryArticle.contentType)}>
                    {primaryArticle.contentType.replace('_', ' ')}
                  </Tag>
                )}
                {primaryArticle.sourceCredibility && (
                  <Tag color={getSourceCredibilityBadge(primaryArticle.sourceCredibility).color}>
                    {getSourceCredibilityBadge(primaryArticle.sourceCredibility).text}
                  </Tag>
                )}
                <Tag color={getSentimentColor(primaryArticle.sentiment)}>
                  {primaryArticle.sentiment?.label || 'neutral'}
                </Tag>
                {primaryArticle.relevanceScore > 0 && (
                  <Tag color={getRelevanceColor(primaryArticle.relevanceScore)}>
                    {Math.round(primaryArticle.relevanceScore * 100)}% relevant
                  </Tag>
                )}
                {primaryArticle.location && (
                  <Tag>{primaryArticle.location}</Tag>
                )}
                <Tag icon={<ClockCircleOutlined />}>
                  {formatTimeAgo(primaryArticle.publishedAt)}
                </Tag>
              </Space>
            </Space>
          }
          description={
            <div>
              {primaryArticle.content && (
                <Paragraph 
                  ellipsis={{ rows: 2, expandable: false }}
                  style={{ marginBottom: 8, color: '#666' }}
                >
                  {primaryArticle.content}
                </Paragraph>
              )}
              
              {/* Expanded supporting articles */}
              {expanded && supportingArticles.length > 0 && (
                <div style={{ 
                  marginTop: 16, 
                  padding: 16, 
                  backgroundColor: '#fff',
                  borderRadius: 6,
                  border: '1px solid #e6f7ff'
                }}>
                  <Text strong style={{ marginBottom: 12, display: 'block' }}>
                    Supporting Coverage from {supportingArticles.length} additional source{supportingArticles.length > 1 ? 's' : ''}:
                  </Text>
                  {supportingArticles.map((supportingArticle, index) => (
                    <div key={supportingArticle.id || index} style={{ 
                      marginBottom: 12,
                      padding: 12,
                      backgroundColor: '#f9f9f9',
                      borderRadius: 4,
                      borderLeft: '3px solid #1890ff'
                    }}>
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Text strong style={{ fontSize: 14 }}>
                            {supportingArticle.title}
                          </Text>
                          <Button 
                            type="link" 
                            size="small"
                            icon={<LinkOutlined />}
                            href={supportingArticle.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          />
                        </div>
                        <Space wrap size={4}>
                          <Tag color="blue" size="small">
                            {supportingArticle.source}
                          </Tag>
                          {supportingArticle.relevanceScore > 0 && (
                            <Tag color={getRelevanceColor(supportingArticle.relevanceScore)} size="small">
                              {Math.round(supportingArticle.relevanceScore * 100)}%
                            </Tag>
                          )}
                          <Tag size="small">
                            {Math.round(supportingArticle.similarityScore * 100)}% similar
                          </Tag>
                          <Tag icon={<ClockCircleOutlined />} size="small">
                            {formatTimeAgo(supportingArticle.publishedAt)}
                          </Tag>
                        </Space>
                      </Space>
                    </div>
                  ))}
                </div>
              )}
            </div>
          }
        />
      </List.Item>
    );
  };

  /**
   * Render a regular article item (for standalone articles or fallback)
   */
  const renderArticleItem = (article) => {
    return (
      <List.Item
        key={article.id}
        actions={[
          <Tooltip title="View full article">
            <Button 
              type="link" 
              icon={<LinkOutlined />}
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Read More
            </Button>
          </Tooltip>
        ]}
      >
        <List.Item.Meta
          avatar={
            <div style={{ textAlign: 'center' }}>
              {article.crisisIndicators?.length > 0 && (
                <AlertOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
              )}
              {article.relevanceScore >= 0.7 && (
                <FireOutlined style={{ color: '#fa8c16', fontSize: 16 }} />
              )}
            </div>
          }
          title={
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Text strong style={{ fontSize: 16 }}>
                {article.title}
              </Text>
              <Space wrap>
                <Tag color="blue" icon={<GlobalOutlined />}>
                  {article.source}
                </Tag>
                {article.feedSource && (
                  <Tag color="cyan">
                    {article.feedSource}
                  </Tag>
                )}
                {article.contentType && (
                  <Tag color={getContentTypeColor(article.contentType)}>
                    {article.contentType.replace('_', ' ')}
                  </Tag>
                )}
                {article.sourceCredibility && (
                  <Tag color={getSourceCredibilityBadge(article.sourceCredibility).color}>
                    {getSourceCredibilityBadge(article.sourceCredibility).text}
                  </Tag>
                )}
                <Tag color={getSentimentColor(article.sentiment)}>
                  {article.sentiment?.label || 'neutral'}
                </Tag>
                {article.relevanceScore > 0 && (
                  <Tag color={getRelevanceColor(article.relevanceScore)}>
                    {Math.round(article.relevanceScore * 100)}% relevant
                  </Tag>
                )}
                {article.location && (
                  <Tag>{article.location}</Tag>
                )}
                <Tag icon={<ClockCircleOutlined />}>
                  {formatTimeAgo(article.publishedAt)}
                </Tag>
                {article.filtered && (
                  <Tag color="red">
                    Filtered: {article.filterReason}
                  </Tag>
                )}
              </Space>
            </Space>
          }
          description={
            <div>
              {article.content && (
                <Paragraph 
                  ellipsis={{ rows: 2, expandable: false }}
                  style={{ marginBottom: 8, color: '#666' }}
                >
                  {article.content}
                </Paragraph>
              )}
            </div>
          }
        />
      </List.Item>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text>Loading media intelligence...</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Media Intelligence Error"
        description={error}
        type="error"
        showIcon
        action={
          <Button size="small" onClick={handleRefresh}>
            Retry
          </Button>
        }
      />
    );
  }

  return (
    <div style={{ padding: '16px' }}>
      <style>
        {`
          .media-intelligence-dashboard .ant-collapse-header {
            color: rgba(0, 0, 0, 0.85) !important;
            cursor: default !important;
          }
          .media-intelligence-dashboard .ant-collapse-header .ant-collapse-arrow {
            color: rgba(0, 0, 0, 0.45) !important;
          }
        `}
      </style>
      <Collapse 
        activeKey={activeAccordionKeys}
        onChange={setActiveAccordionKeys}
        ghost
        size="large"
      >
        {/* Section 1: Metrics Dashboard (Always Expanded) */}
        <Panel 
          header={
            <Space>
              <BarChartOutlined />
              <strong>Media Intelligence Dashboard</strong>
              <Badge count={articles.length} style={{ backgroundColor: '#52c41a' }} />
            </Space>
          } 
          key="metrics"
          collapsible="disabled"
          className="media-intelligence-dashboard"
        >
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card>
                <Row gutter={16}>
                  <Col span={6}>
                    <Tooltip title="Total number of media articles found and analyzed from news sources, RSS feeds, and online publications. This includes articles that match your organisation and crisis-specific keywords.">
                      <Statistic
                        title={<span style={{ fontWeight: 'bold', color: '#000', cursor: 'help' }}>Articles Monitored</span>}
                        value={articles.length}
                        prefix={<GlobalOutlined />}
                      />
                    </Tooltip>
                  </Col>
                  <Col span={6}>
                    <Tooltip title="Number of articles containing crisis indicators such as emergency terms, urgency keywords, or direct references to your incident type. These articles may require immediate attention.">
                      <Statistic
                        title={<span style={{ fontWeight: 'bold', color: '#000', cursor: 'help' }}>Crisis Indicators</span>}
                        value={articles.filter(a => a.crisisIndicators?.length > 0).length}
                        prefix={<AlertOutlined />}
                        valueStyle={{ color: '#cf1322' }}
                      />
                    </Tooltip>
                  </Col>
                  <Col span={6}>
                    <Tooltip title="Articles with 70%+ relevance score to your organisation and current crisis. These articles are most likely to contain important information about your incident or related impact.">
                      <Statistic
                        title={<span style={{ fontWeight: 'bold', color: '#000', cursor: 'help' }}>High Relevance</span>}
                        value={articles.filter(a => a.relevanceScore >= 0.7).length}
                        prefix={<FireOutlined />}
                        valueStyle={{ color: '#fa8c16' }}
                      />
                    </Tooltip>
                  </Col>
                  <Col span={6}>
                    <div style={{ textAlign: 'center' }}>
                      <Tooltip title="Real-time monitoring status. Active means the system is continuously scanning news sources for new articles. Click Refresh to manually update or check connection status.">
                        <span style={{ fontWeight: 'bold', color: '#000', cursor: 'help' }}>Monitoring Status</span>
                      </Tooltip>
                      <div style={{ marginTop: 8 }}>
                        <Badge 
                          status={metrics.isRunning ? "processing" : "default"} 
                          text={metrics.isRunning ? "Active" : "Inactive"}
                        />
                      </div>
                      <div style={{ marginTop: 4 }}>
                        <Tooltip title="Manually refresh media monitoring to fetch the latest articles and update metrics. The system also automatically checks for new content periodically.">
                          <Button size="small" onClick={handleRefresh}>
                            Refresh Now
                          </Button>
                        </Tooltip>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Panel>

        {/* Section 2: Media Coverage */}
        <Panel 
          header={
            <Space>
              <GlobalOutlined />
              <strong>Recent Media Coverage</strong>
              <Badge count={articles.length} style={{ backgroundColor: '#1890ff' }} />
              {metrics.lastRunTime && (
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Last updated: {new Date(metrics.lastRunTime).toLocaleTimeString()}
                </Text>
              )}
              <Divider type="vertical" />
              <Tooltip title={useClusteredView ? "Switch to flat list view" : "Switch to clustered view - groups similar articles from different sources"}>
                <Space size={8} onClick={(e) => e.stopPropagation()}>
                  <Text style={{ fontSize: '12px', color: '#666' }}>
                    {useClusteredView ? 'Clustered' : 'Flat'} View
                  </Text>
                  <Switch
                    size="small"
                    checked={useClusteredView}
                    onChange={setUseClusteredView}
                    checkedChildren="📊"
                    unCheckedChildren="📝"
                  />
                </Space>
              </Tooltip>
            </Space>
          } 
          key="coverage"
        >
          {articles.length === 0 ? (
            <Empty 
              description={
                <div style={{ textAlign: 'left', maxWidth: 600, margin: '0 auto' }}>
                  <Text strong style={{ fontSize: 16, display: 'block', textAlign: 'center', marginBottom: 16 }}>
                    No relevant media articles found in the following data sources:
                  </Text>
                  
                  {lastSearchSources.length > 0 ? (
                    <div style={{ marginBottom: 16 }}>
                      {lastSearchSources.map((source, index) => (
                        <div key={index} style={{ marginBottom: 8, padding: 8, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <Text strong>{source.name}</Text>
                              {source.provider && (
                                <Tag color="blue" style={{ marginLeft: 8, fontSize: 10 }}>
                                  {source.provider}
                                </Tag>
                              )}
                            </div>
                            <Space>
                              {source.status === 'success' && (
                                <Tag color="green">✅ {source.articleCount || source.articlesFound || 0} articles</Tag>
                              )}
                              {source.status === 'no_results' && (
                                <Tag color="blue">📄 No articles found</Tag>
                              )}
                              {source.status === 'error' && (
                                <Tag color="red">❌ Failed</Tag>
                              )}
                              {source.status === 'attempted' && (
                                <Tag color="orange">⏳ Processing</Tag>
                              )}
                            </Space>
                          </div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Source Type: {source.type || 'RSS Feed'}
                            {source.error && ` • Error: ${source.error}`}
                          </Text>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ marginBottom: 16, textAlign: 'center' }}>
                      <Text type="secondary">
                        • ABC News Australia (Breaking news & emergency feeds)
                        <br />
                        • Sydney Morning Herald (NSW & national coverage)
                        <br />
                        • The Age (Victoria & national coverage)
                        <br />
                        • Nine News (Breaking news & current affairs)
                        <br />
                        • SBS News (Multicultural & national coverage)
                        <br />
                        • News.com.au (National breaking news)
                        <br />
                        • Google News RSS (Crisis-specific searches)
                      </Text>
                    </div>
                  )}
                  
                  <div style={{ textAlign: 'center' }}>
                    <Text type="secondary">
                      This is expected behavior for crisis-specific searches.
                      <br />
                      <Text strong>The system is functioning properly</Text> - there are no relevant media articles returned.
                    </Text>
                  </div>
                </div>
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <div>
              {/* 🔧 SURGICAL PRECISION: Conditional rendering based on cluster toggle */}
              {useClusteredView ? (
              <List
                dataSource={clusterSimilarArticles(articles)}
                renderItem={renderClusteredItem}
                style={{ backgroundColor: '#fff' }}
              />
            ) : (
              <List
                dataSource={sortArticlesByRelevance(articles)}
                renderItem={(article) => (
                <List.Item
                  key={article.id}
                  actions={[
                    <Tooltip title="View full article">
                      <Button 
                        type="link" 
                        icon={<LinkOutlined />}
                        href={article.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Read More
                      </Button>
                    </Tooltip>
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <div style={{ textAlign: 'center' }}>
                        {article.crisisIndicators?.length > 0 && (
                          <AlertOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
                        )}
                        {article.relevanceScore >= 0.7 && (
                          <FireOutlined style={{ color: '#fa8c16', fontSize: 16 }} />
                        )}
                      </div>
                    }
                    title={
                      <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        <Text strong style={{ fontSize: 16 }}>
                          {article.title}
                        </Text>
                        <Space wrap>
                          <Tag color="blue" icon={<GlobalOutlined />}>
                            {article.source}
                          </Tag>
                          {article.feedSource && (
                            <Tag color="cyan">
                              {article.feedSource}
                            </Tag>
                          )}
                          {article.contentType && (
                            <Tag color={getContentTypeColor(article.contentType)}>
                              {article.contentType.replace('_', ' ')}
                            </Tag>
                          )}
                          {article.sourceCredibility && (
                            <Tag color={getSourceCredibilityBadge(article.sourceCredibility).color}>
                              {getSourceCredibilityBadge(article.sourceCredibility).text}
                            </Tag>
                          )}
                          <Tag color={getSentimentColor(article.sentiment)}>
                            {article.sentiment?.label || 'neutral'}
                          </Tag>
                          {article.relevanceScore > 0 && (
                            <Tag color={getRelevanceColor(article.relevanceScore)}>
                              {Math.round(article.relevanceScore * 100)}% relevant
                            </Tag>
                          )}
                          {article.location && (
                            <Tag>{article.location}</Tag>
                          )}
                          <Tag icon={<ClockCircleOutlined />}>
                            {formatTimeAgo(article.publishedAt)}
                          </Tag>
                          {article.filtered && (
                            <Tag color="red">
                              Filtered: {article.filterReason}
                            </Tag>
                          )}
                        </Space>
                      </Space>
                    }
                    description={
                      <div>
                        {article.content && (
                          <Paragraph 
                            ellipsis={{ rows: 2, expandable: false }}
                            style={{ marginBottom: 8 }}
                          >
                            {article.content}
                          </Paragraph>
                        )}
                        
                        {/* Crisis Keywords Matched */}
                        {article.matchedCrisisKeywords && article.matchedCrisisKeywords.length > 0 && (
                          <div style={{ marginBottom: 8 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>Matched Crisis Keywords: </Text>
                            {[...new Set(article.matchedCrisisKeywords)].slice(0, 5).map(keyword => (
                              <Tag key={keyword} size="small" color="red">{keyword}</Tag>
                            ))}
                          </div>
                        )}

                        {/* Match Reasons */}
                        {article.matchReasons && article.matchReasons.length > 0 && (
                          <div style={{ marginBottom: 8 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>Match Reasons: </Text>
                            {article.matchReasons.slice(0, 3).map((reason, idx) => (
                              <Tag key={idx} size="small" color="blue">{reason}</Tag>
                            ))}
                          </div>
                        )}
                        
                        {/* Original Keywords (only show if no crisis keywords matched) */}
                        {(!article.matchedCrisisKeywords || article.matchedCrisisKeywords.length === 0) && 
                         article.keywords && article.keywords.length > 0 && (
                          <div style={{ marginBottom: 8 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>Keywords: </Text>
                            {article.keywords.slice(0, 5).map(keyword => (
                              <Tag key={keyword} size="small">{keyword}</Tag>
                            ))}
                          </div>
                        )}
                        
                        {/* Crisis Indicators */}
                        {article.crisisIndicators && article.crisisIndicators.length > 0 && (
                          <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>Crisis Indicators: </Text>
                            {article.crisisIndicators.map((indicator, idx) => (
                              <Tag 
                                key={idx} 
                                color="red" 
                                size="small"
                                icon={<ThunderboltOutlined />}
                              >
                                {indicator.type}: {indicator.keyword || indicator.category}
                              </Tag>
                            ))}
                          </div>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
            )}
            </div>
          )}
        </Panel>

        {/* Section 3: Configuration & Debug (Merged) */}
        <Panel 
          header={
            <Space>
              <DatabaseOutlined />
              <strong>Configuration and Debug Information</strong>
              <Badge 
                count={`${metrics.filteringStats?.filterRate || '0%'} filtered`} 
                style={{ backgroundColor: '#722ed1' }} 
              />
            </Space>
          } 
          key="config"
        >
          {/* Content Filtering and Quality Metrics */}
          <Card 
            title={
              <Space>
                <FilterOutlined />
                Content Filtering and Quality Metrics
              </Space>
            }
            style={{ marginBottom: 16 }}
          >
            <Row gutter={16}>
              {/* Filtering Stats */}
              <Col span={5}>
                <Statistic
                  title="Articles Processed"
                  value={metrics.filteringStats?.totalProcessed || 0}
                  prefix={<EyeOutlined />}
                />
              </Col>
              <Col span={5}>
                <Statistic
                  title="Articles Filtered"
                  value={metrics.filteringStats?.totalFiltered || 0}
                  prefix={<FilterOutlined />}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="Filter Rate"
                  value={metrics.filteringStats?.filterRate || '0%'}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Col>
              <Col span={5}>
                <Statistic
                  title="Avg Relevance"
                  value={metrics.filteringStats?.avgRelevanceScore || '0.000'}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col span={5}>
                <div style={{ textAlign: 'center' }}>
                  <Text strong>Filter Sensitivity</Text>
                  <div style={{ marginTop: 8 }}>
                    <Tooltip title="Adjust how strictly articles are filtered based on relevance. Strict mode shows only highly relevant content, while relaxed mode includes broader coverage that might be tangentially related.">
                      <Select
                        value={filterSensitivity}
                        onChange={handleFilterSensitivityChange}
                        style={{ width: '100%' }}
                        size="small"
                      >
                        <Option value="strict">🔒 Strict (75%+ relevance) - HIGHLY RELEVANT ONLY</Option>
                        <Option value="medium">⚖️ Medium (50%+ relevance) - CRISIS SPECIFIC</Option>
                        <Option value="relaxed">🔓 Relaxed (30%+ relevance) - BROADER SEARCH</Option>
                      </Select>
                    </Tooltip>
                  </div>
                </div>
              </Col>
            </Row>
            
            <Divider />
            
            {/* Content Type Breakdown */}
            <Row gutter={16}>
              <Col span={12}>
                <Text strong>Content Types Found:</Text>
                <div style={{ marginTop: 8 }}>
                  <Space wrap>
                    {['breaking_news', 'crisis_news', 'general_news', 'celebrity', 'podcast', 'entertainment', 'sports'].map(type => {
                      const count = articles.filter(a => a.contentType === type).length;
                      return count > 0 ? (
                        <Tag key={type} color={getContentTypeColor(type)}>
                          {type.replace('_', ' ')}: {count}
                        </Tag>
                      ) : null;
                    })}
                  </Space>
                </div>
                {articles.filter(a => ['celebrity', 'entertainment', 'sports'].includes(a.contentType)).length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="warning" style={{ fontSize: 12 }}>
                      ⚠️ {articles.filter(a => ['celebrity', 'entertainment', 'sports'].includes(a.contentType)).length} irrelevant articles detected
                    </Text>
                  </div>
                )}
              </Col>
              
              <Col span={12}>
                <Text strong>Source Quality:</Text>
                <div style={{ marginTop: 8 }}>
                  <Space wrap>
                    {['high', 'medium', 'low'].map(credibility => {
                      const count = articles.filter(a => a.sourceCredibility === credibility).length;
                      const badge = getSourceCredibilityBadge(credibility);
                      return count > 0 ? (
                        <Tag key={credibility} color={badge.color}>
                          {badge.text}: {count}
                        </Tag>
                      ) : null;
                    })}
                  </Space>
                </div>
              </Col>
            </Row>
          </Card>

          {/* Debug Info (Development Only) */}
          {process.env.NODE_ENV === 'development' && (
            <Card 
              title="🔧 Debug Information" 
              size="small" 
              type="inner"
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Text strong>Service Metrics:</Text>
                  <ul style={{ fontSize: 12 }}>
                    <li>Articles Processed: {metrics.articlesProcessed || 0}</li>
                    <li>Duplicates Filtered: {metrics.duplicatesFiltered || 0}</li>
                    <li>Alerts Generated: {metrics.alertsGenerated || 0}</li>
                    <li>Errors: {metrics.errorCount || 0}</li>
                    <li>Processing Time: {metrics.processingTime?.toFixed(2) || 0}ms</li>
                  </ul>
                  
                  {/* DEMO BUTTON */}
                  <div style={{ marginTop: 16 }}>
                    <Button 
                      type="primary" 
                      danger 
                      onClick={addDemoSystemMessage}
                      style={{ fontSize: 12 }}
                    >
                      🎭 Add Demo System Message
                    </Button>
                    <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>
                      Adds a sample Media Intelligence alert to live chat
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <Text strong>Search Configuration:</Text>
                  <ul style={{ fontSize: 12 }}>
                    <li><strong>Crisis Type:</strong> {incident?.crisisType || 'Loading...'}</li>
                    <li><strong>Crisis Location:</strong> {incident?.location || 'Loading...'}</li>
                    <li><strong>Organisation:</strong> {organisationData?.name || currentUser?.organisationName || 'Loading...'}</li>
                                          <li><strong>Search Terms:</strong> {organisationData && incident ? buildCrisisSpecificSearchTerms(organisationData, incident).slice(0, 3).join(', ') + '...' : 'Loading...'}</li>
                {expandedKeywords.length > 0 && (
                  <li><strong>GPT Keywords:</strong> {expandedKeywords.slice(0, 5).join(', ')} {expandedKeywords.length > 5 && `(+${expandedKeywords.length - 5} more)`}</li>
                )}
                    <li><strong>Providers:</strong> {metrics.providersCount || 0}</li>
                    <li><strong>Processors:</strong> {metrics.processorsCount || 0}</li>
                  </ul>
                  
                  {/* Show problematic articles */}
                  {articles.filter(a => ['celebrity', 'entertainment', 'sports'].includes(a.contentType)).length > 0 && (
                    <div style={{ marginTop: 8, padding: 8, backgroundColor: '#fff2e8', border: '1px solid #ffbb96' }}>
                      <Text strong style={{ color: '#d46b08' }}>⚠️ Problematic Articles Detected:</Text>
                      <ul style={{ fontSize: 11, margin: '4px 0', paddingLeft: 16 }}>
                        {articles.filter(a => ['celebrity', 'entertainment', 'sports'].includes(a.contentType)).map(article => (
                          <li key={article.id} style={{ marginBottom: 2 }}>
                            <strong>{article.contentType}:</strong> {article.title?.substring(0, 60)}...
                            {article.matchedCrisisKeywords?.length > 0 && (
                              <span style={{ color: 'red' }}> (Matched: {article.matchedCrisisKeywords.join(', ')})</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </Col>
              </Row>
            </Card>
          )}
        </Panel>
      </Collapse>
    </div>
  );
};

export default MediaIntelligenceTab; 